import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { MapPin, Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Checkbox from '../../components/common/Checkbox';
import Skeleton from '../../components/common/Skeleton';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { addressService } from '../../services/addressService';
import { fetchCepData, unmask } from '../../utils/maskHelpers';

const EMPTY_ADDR = {
  label: 'Casa',
  recipientName: '',
  zip: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  country: 'BR',
  isDefault: false,
};

const AddressForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || { ...EMPTY_ADDR });
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const { error, warning } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCepBlur = useCallback(async () => {
    if (unmask(form.zip).length !== 8) return;
    setCepLoading(true);
    try {
      const data = await fetchCepData(form.zip);
      if (data) {
        setForm((f) => ({
          ...f,
          street: f.street || data.logradouro,
          district: f.district || data.bairro,
          city: f.city || data.localidade,
          state: f.state || data.uf,
        }));
      } else {
        warning('CEP não encontrado — preencha manualmente.');
      }
    } finally {
      setCepLoading(false);
    }
  }, [form.zip, warning]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.zip || !form.street || !form.city || form.state?.length !== 2) {
      error('Preencha CEP, rua, cidade e UF.');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 bg-app-surface rounded-lg">
      <Input
        label="Rótulo"
        className="md:col-span-2"
        value={form.label || ''}
        onChange={(e) => set('label', e.target.value)}
      />
      <Input
        label="Destinatário"
        className="md:col-span-4"
        value={form.recipientName || ''}
        onChange={(e) => set('recipientName', e.target.value)}
      />
      <div className="md:col-span-2">
        <MaskedInput
          label="CEP"
          mask="cep"
          value={form.zip}
          onChange={(e) => set('zip', e.target.value)}
          onBlur={handleCepBlur}
          required
          helpText={cepLoading ? 'Buscando…' : 'Autopreenche ao digitar'}
        />
      </div>
      <Input
        label="Rua"
        className="md:col-span-3"
        value={form.street}
        onChange={(e) => set('street', e.target.value)}
        required
      />
      <Input
        label="Número"
        className="md:col-span-1"
        value={form.number || ''}
        onChange={(e) => set('number', e.target.value)}
      />
      <Input
        label="Complemento"
        className="md:col-span-3"
        value={form.complement || ''}
        onChange={(e) => set('complement', e.target.value)}
      />
      <Input
        label="Bairro"
        className="md:col-span-3"
        value={form.district || ''}
        onChange={(e) => set('district', e.target.value)}
      />
      <Input
        label="Cidade"
        className="md:col-span-4"
        value={form.city}
        onChange={(e) => set('city', e.target.value)}
        required
      />
      <Input
        label="UF"
        className="md:col-span-1"
        maxLength={2}
        value={form.state}
        onChange={(e) => set('state', e.target.value.toUpperCase())}
        required
      />
      <Input
        label="País"
        className="md:col-span-1"
        maxLength={2}
        value={form.country || 'BR'}
        onChange={(e) => set('country', e.target.value.toUpperCase())}
      />
      <div className="md:col-span-6 pt-1">
        <Checkbox
          label="Usar como endereço padrão"
          checked={!!form.isDefault}
          onChange={(e) => set('isDefault', e.target.checked)}
        />
      </div>
      <div className="md:col-span-6 flex gap-2 mt-2">
        <Button type="button" variant="subtle" fullWidth onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" fullWidth loading={saving}>
          Salvar endereço
        </Button>
      </div>
    </form>
  );
};

const ClientAddresses = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { error, success } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setRows((await addressService.list()) || []);
    } catch (e) {
      error(e.message);
    } finally {
      setBusy(false);
    }
  }, [error]);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading) return <Skeleton variant="block" height={120} />;
  if (!isAuthenticated) return <Navigate to="/entrar" replace />;

  const create = async (payload) => {
    try {
      await addressService.create(payload);
      success('Endereço criado.');
      setCreating(false);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  const update = async (id, payload) => {
    try {
      await addressService.update(id, payload);
      success('Endereço atualizado.');
      setEditing(null);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await addressService.remove(toDelete);
      success('Endereço excluído.');
      setToDelete(null);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Link
        to="/catalogo"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mb-3"
      >
        <ArrowLeft size={14} /> Voltar à loja
      </Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Meus endereços</h1>
        {!creating && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setCreating(true)}>
            Novo endereço
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-4 mb-4">
          <h2 className="font-semibold text-app-text mb-3">Novo endereço</h2>
          <AddressForm onSave={create} onCancel={() => setCreating(false)} />
        </Card>
      )}

      {busy && <Skeleton variant="block" height={100} />}

      {!busy && rows.length === 0 && !creating && (
        <Card>
          <EmptyState
            icon={MapPin}
            title="Nenhum endereço cadastrado"
            description="Adicione um endereço pra agilizar seus próximos pedidos."
            action={<Button onClick={() => setCreating(true)}>Criar endereço</Button>}
          />
        </Card>
      )}

      <ul className="space-y-3">
        {rows.map((a) => (
          <li key={a.id}>
            {editing === a.id ? (
              <Card className="p-4">
                <AddressForm
                  initial={a}
                  onSave={(payload) => update(a.id, payload)}
                  onCancel={() => setEditing(null)}
                />
              </Card>
            ) : (
              <Card className="p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-app-text">{a.label || 'Endereço'}</span>
                      {a.isDefault && (
                        <Badge color="success" size="sm" variant="soft">
                          Padrão
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-app-text mt-1">
                      {a.street}, {a.number || 's/n'}
                      {a.complement ? ` — ${a.complement}` : ''}
                    </p>
                    <p className="text-sm text-app-text-secondary">
                      {a.district ? `${a.district}, ` : ''}
                      {a.city} - {a.state} · CEP {a.zip}
                    </p>
                    {a.recipientName && (
                      <p className="text-xs text-app-text-secondary mt-1">
                        Destinatário: {a.recipientName}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="subtle"
                      leftIcon={<Edit size={14} />}
                      onClick={() => setEditing(a.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      leftIcon={<Trash2 size={14} />}
                      onClick={() => setToDelete(a.id)}
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir endereço?"
        message="Esse endereço não poderá ser recuperado."
        confirmLabel="Excluir"
        variant="danger"
      />
    </div>
  );
};

export default ClientAddresses;
